import { forwardRef } from "react";
import { API } from "@/lib/api";

/**
 * SmartImage — renders a portfolio/editorial image with:
 *  - lazy loading (native)
 *  - object-fit: cover (no distortion)
 *  - object-position driven by the photo's focal point (focal_x/focal_y, 0..1)
 *  - optional fixed aspect ratio (e.g. "aspect-[4/5]") or "natural" (intrinsic aspect)
 *  - WebP srcset with retina buckets (up to 2400px) via /api/img/{path}?w=…
 *  - high-DPI hint via `image-rendering: high-quality`
 *
 * Props:
 *  - photo: { storage_path, focal_x?, focal_y?, width?, height?, title? }
 *  - aspect: tailwind aspect class string OR null for natural
 *  - className: extra classes for the wrapper
 *  - imgClassName: extra classes for the <img>
 *  - eager: when true, disables lazy loading (use only for above-the-fold hero)
 *  - sizes: HTML `sizes` attribute hint (defaults to a sensible responsive heuristic)
 *  - quality: optional override (default 86)
 */
const WIDTHS = [320, 480, 640, 800, 1024, 1280, 1600, 2000, 2400];

function buildSrcSet(path, q) {
  return WIDTHS.map((w) => `${API}/img/${path}?w=${w}&q=${q} ${w}w`).join(", ");
}

const SmartImage = forwardRef(function SmartImage(
  { photo, aspect = "aspect-[4/5]", className = "", imgClassName = "", eager = false, alt = "", sizes, quality = 86 },
  ref
) {
  if (!photo?.storage_path) {
    return <div className={`bg-[#FAF3EA] ${aspect || ""} ${className}`} ref={ref} />;
  }
  const fx = typeof photo.focal_x === "number" ? photo.focal_x : 0.5;
  const fy = typeof photo.focal_y === "number" ? photo.focal_y : 0.5;
  const objectPosition = `${(fx * 100).toFixed(2)}% ${(fy * 100).toFixed(2)}%`;
  const srcSet = buildSrcSet(photo.storage_path, quality);
  const fallback = `${API}/img/${photo.storage_path}?w=1600&q=${quality}`;
  const sensibleSizes = sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

  const commonProps = {
    src: fallback,
    srcSet,
    sizes: sensibleSizes,
    alt: alt || photo.title || "",
    loading: eager ? "eager" : "lazy",
    decoding: "async",
    fetchPriority: eager ? "high" : "auto",
    width: photo.width || undefined,
    height: photo.height || undefined,
    style: { objectPosition, imageRendering: "high-quality" },
  };

  // Natural mode: container has no fixed aspect; <img> drives the height.
  if (!aspect) {
    return (
      <div ref={ref} className={`overflow-hidden bg-[#FAF3EA] ${className}`}>
        <img {...commonProps} className={`block w-full h-auto ${imgClassName}`} />
      </div>
    );
  }

  return (
    <div ref={ref} className={`overflow-hidden bg-[#FAF3EA] ${aspect} ${className}`}>
      <img {...commonProps} className={`block w-full h-full object-cover ${imgClassName}`} />
    </div>
  );
});

export default SmartImage;
